import type { FlagKind } from 'definitions';
import type { FlagsListManager } from '@/state/flags/useFlags';
import type { CreateFlagManager } from '@/state/flags/useCreateFlag';
import { useNavigate } from 'react-router';
import { Lock } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateFlag } from '@/state/flags/useCreateFlag';
import { apiErrorMessage } from '@/state/api-error';
import { HelpTip } from '../Experiments/Details/ExperimentSettings/TargetingHelp';
import { FLAG_COPY } from './copy';

type CreateFlagDialogProps = {
  list: FlagsListManager;
  onClose: () => void;
};

const FIELD_CLASS = 'border-input bg-field text-foreground h-9 rounded-lg text-body';
const TEXTAREA_CLASS = 'border-input bg-field text-foreground min-h-[76px] resize-y rounded-lg text-body';
const PAYLOAD_CLASS = 'border-input bg-field text-foreground h-9 rounded-lg font-mono text-meta';
const CHIP_CLASS =
  'border-line bg-panel text-dim rounded-pill border px-2 py-0.5 font-mono text-nano tracking-[0.1em] uppercase';

/** Editor first: the quick fix is the case most people come for. */
const KINDS: readonly FlagKind[] = ['editor', 'code'];

const COPY = FLAG_COPY.create;

/**
 * Creates a flag as a draft and opens it, in the Create experiment dialog's structure: how it
 * runs, the name, the key, a description, then the two sides in the block the experiment
 * dialog gives its variants. Off is fixed, as Original is there; a code flag's sides each take
 * a payload, and the flag what to bucket on. An editor flag needs neither.
 */
export const CreateFlagDialog = ({ list, onClose }: CreateFlagDialogProps) => {
  const navigate = useNavigate();
  const form = useCreateFlag(list);

  const onConfirm = async () => {
    try {
      const flag = await form.submit();
      if (!flag) return;
      onClose();
      navigate(`/flags/${flag.id}`);
    } catch (error) {
      // The dialog stays open with everything typed, to correct.
      toast.error(apiErrorMessage(error, COPY.failed));
    }
  };

  return (
    <ConfirmDialog
      open
      title={COPY.title}
      description={COPY.intro}
      confirmLabel={COPY.title}
      disabled={form.isSubmitting || !!form.blockingReason}
      disabledReason={form.blockingReason}
      onConfirm={onConfirm}
      onCancel={onClose}
    >
      <div className="grid gap-4">
        {/* The kind first, because it decides what the rest of the form asks for. */}
        <fieldset className="grid gap-2">
          <legend className="micro-label mb-2">{COPY.kind}</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {KINDS.map((option) => {
              const isSelected = form.kind === option;
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => form.setKind(option)}
                  className={cn(
                    'focus-visible:ring-ring rounded-card border p-3 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none',
                    isSelected
                      ? 'border-sel-line bg-sel-bg'
                      : 'border-input hover:bg-hover text-dim hover:text-foreground',
                  )}
                >
                  <span className={cn('block text-body font-medium', isSelected && 'text-foreground')}>
                    {COPY.kinds[option].label}
                  </span>
                  <span className="supporting mt-1 block">{COPY.kinds[option].description}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="grid gap-1.5">
          <label htmlFor="flag-name" className="micro-label">
            {COPY.name}
          </label>
          <Input
            id="flag-name"
            value={form.name}
            maxLength={256}
            onChange={(ev) => form.setName(ev.target.value)}
            placeholder={COPY.namePlaceholder}
            className={FIELD_CLASS}
          />
        </div>

        <div className="grid gap-1.5">
          <label htmlFor="flag-key" className="micro-label">
            {COPY.key}
          </label>
          <Input
            id="flag-key"
            value={form.flagKey}
            maxLength={64}
            onChange={(ev) => form.setFlagKey(ev.target.value)}
            placeholder="new-checkout"
            aria-invalid={form.keyError}
            aria-describedby="flag-key-help"
            className={cn(FIELD_CLASS, 'font-mono')}
          />
          <p id="flag-key-help" className={cn('supporting m-0', form.keyError && 'text-danger')}>
            {form.keyError ? COPY.keyInvalid : COPY.keyHelp}
          </p>
        </div>

        <div className="grid gap-1.5">
          <label htmlFor="flag-description" className="micro-label">
            {COPY.description}
          </label>
          <Textarea
            id="flag-description"
            value={form.description}
            onChange={(ev) => form.setDescription(ev.target.value)}
            placeholder={COPY.descriptionPlaceholder}
            className={TEXTAREA_CLASS}
          />
        </div>

        <SidesBlock form={form} />
      </div>
    </ConfirmDialog>
  );
};

type SidesBlockProps = { form: CreateFlagManager };

/**
 * The two sides, in the block the experiment dialog gives its variants. Off is added for you,
 * as Original is, so it is drawn locked; On follows it. Nothing is added or removed here: a flag
 * always has exactly these two.
 */
const SidesBlock = ({ form }: SidesBlockProps) => (
  <fieldset className="border-line rounded-card grid gap-2 border p-3">
    {/* Inside a div, as on the experiment dialog, so it is not drawn on the fieldset's border. */}
    <div className="flex min-h-7 items-center">
      <legend className="micro-label">{COPY.sides}</legend>
    </div>

    <div className="bg-field border-line grid gap-2 rounded-lg border border-dashed px-2.5 py-1.5">
      <div className="flex min-h-8 items-start gap-2">
        <Lock className="text-dim mt-1 size-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
        <div className="grid min-w-0 gap-0.5">
          <div className="flex items-center gap-2">
            <span className="text-foreground text-body font-medium">{COPY.off}</span>
            <span className={CHIP_CLASS}>{COPY.offChip}</span>
          </div>
          <span className="supporting">{COPY.offNote[form.kind]}</span>
        </div>
      </div>
      {form.isCode && (
        <PayloadField
          id="flag-off-payload"
          side={COPY.off}
          value={form.offPayloadText}
          onChange={form.setOffPayloadText}
          invalid={form.offPayloadInvalid}
        />
      )}
    </div>

    <div className="border-line grid gap-2 rounded-lg border px-2.5 py-1.5">
      <div className="grid min-h-8 min-w-0 gap-0.5 pl-[22px]">
        <span className="text-foreground text-body font-medium">{COPY.on}</span>
        <span className="supporting">{COPY.onNote[form.kind]}</span>
      </div>
      {form.isCode && (
        <PayloadField
          id="flag-on-payload"
          side={COPY.on}
          value={form.onPayloadText}
          onChange={form.setOnPayloadText}
          invalid={form.onPayloadInvalid}
        />
      )}
    </div>

    {form.isCode && (
      <div className="grid gap-1.5 pt-1 sm:max-w-[50%]">
        <span className="flex items-center gap-1.5">
          <span className="micro-label">{COPY.bucketOn}</span>
          <HelpTip text={COPY.bucketHelp} label={COPY.bucketOn.toLowerCase()} />
        </span>
        <Select value={form.bucketOn} onValueChange={(value) => form.setBucketOn(value as typeof form.bucketOn)}>
          <SelectTrigger className={FIELD_CLASS} aria-label={COPY.bucketOn}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="visitor">{COPY.visitor}</SelectItem>
            <SelectItem value="customer_id">{COPY.customerId}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}
  </fieldset>
);

type PayloadFieldProps = {
  id: string;
  side: string;
  value: string;
  onChange: (value: string) => void;
  invalid: boolean;
};

const PayloadField = ({ id, side, value, onChange, invalid }: PayloadFieldProps) => (
  <div className="grid gap-1 pb-1.5 pl-[22px]">
    <label htmlFor={id} className="micro-label">
      {COPY.payload}
    </label>
    {/* One line, as a variant's name is on the experiment dialog: a payload is usually a few keys. */}
    <Input
      id={id}
      value={value}
      onChange={(ev) => onChange(ev.target.value)}
      placeholder={COPY.payloadPlaceholder}
      spellCheck={false}
      aria-invalid={invalid}
      className={PAYLOAD_CLASS}
    />
    {invalid && (
      <p className="supporting text-danger m-0">
        {side} {COPY.payloadInvalid}
      </p>
    )}
  </div>
);
